import React from "react";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Repeat } from "lucide-react";

const FREQ = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];

/**
 * Standalone control for the "recurring event" checkbox + options.
 * value = { is_recurring: bool, recurrence: { frequency, interval, end_on, max_occurrences } | null }
 * onChange receives the same shape.
 */
export default function RecurrenceControl({ value, onChange }) {
  const isRec = !!value?.is_recurring;
  const rec = value?.recurrence || { frequency: "monthly", interval: 1, end_on: "", max_occurrences: "" };

  const toggle = (checked) => {
    onChange({
      is_recurring: !!checked,
      recurrence: checked ? rec : null,
    });
  };

  const setRec = (patch) => {
    onChange({
      is_recurring: true,
      recurrence: { ...rec, ...patch },
    });
  };

  return (
    <div className="rounded-lg bevel border-hair">
      <label className="flex items-center gap-3 p-3 cursor-pointer">
        <Checkbox
          data-testid="recurrence-toggle"
          checked={isRec}
          onCheckedChange={toggle}
          className="rounded-sm border-hair-strong data-[state=checked]:bg-app data-[state=checked]:border-[#0A0A0A]"
        />
        <div className="flex items-center gap-2 text-sm text-app">
          <Repeat size={14} strokeWidth={1.5} className="text-muted" />
          <span>Recurring event</span>
        </div>
      </label>

      {isRec && (
        <div className="p-3 pt-0 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow block mb-1.5">Frequency</Label>
              <Select value={rec.frequency} onValueChange={(v) => setRec({ frequency: v })}>
                <SelectTrigger data-testid="recurrence-frequency" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow block mb-1.5">Every</Label>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="recurrence-interval"
                  type="number" min={1} max={52}
                  value={rec.interval || 1}
                  onChange={(e) => setRec({ interval: Math.max(1, Number(e.target.value) || 1) })}
                  className="rounded-sm h-10 w-20"
                />
                <span className="text-sm text-muted">{unitLabel(rec.frequency, rec.interval || 1)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow block mb-1.5">Ends on (optional)</Label>
              <Input
                data-testid="recurrence-end-date"
                type="date"
                value={rec.end_on || ""}
                onChange={(e) => setRec({ end_on: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div>
              <Label className="eyebrow block mb-1.5">Or after (occurrences)</Label>
              <Input
                data-testid="recurrence-max-occurrences"
                type="number" min={1}
                placeholder="e.g. 12"
                value={rec.max_occurrences || ""}
                onChange={(e) => setRec({ max_occurrences: e.target.value ? Number(e.target.value) : "" })}
                className="rounded-sm"
              />
            </div>
          </div>

          <p className="text-xs text-muted font-light">
            Recurrence is displayed for planning reference. Individual occurrences aren&apos;t auto-generated — create additional events as needed.
          </p>
        </div>
      )}
    </div>
  );
}

function unitLabel(freq, n) {
  const plural = n > 1;
  return {
    weekly: plural ? "weeks" : "week",
    biweekly: plural ? "bi-weeks" : "bi-week",
    monthly: plural ? "months" : "month",
    quarterly: plural ? "quarters" : "quarter",
    yearly: plural ? "years" : "year",
  }[freq] || "";
}

// Helper to render a compact "Every 2 weeks · until 2026-08-01" summary anywhere in the UI.
export function summarizeRecurrence(ev) {
  if (!ev?.is_recurring || !ev?.recurrence) return null;
  const r = ev.recurrence;
  const freqLabel = { weekly: "week", biweekly: "bi-week", monthly: "month", quarterly: "quarter", yearly: "year" }[r.frequency] || r.frequency;
  const every = (r.interval || 1) === 1 ? `Every ${freqLabel}` : `Every ${r.interval} ${freqLabel}s`;
  let end = "";
  if (r.end_on) end = ` · until ${new Date(r.end_on).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
  else if (r.max_occurrences) end = ` · ${r.max_occurrences} times`;
  return every + end;
}
